// apps/web/src/features/commercial-proposals/formModel.ts
//
// @aiPurpose the editor form model + (de)serialization between the editable
//   form state and the shared `CommercialProposalContent` (#1198). Numeric
//   inputs are held as raw strings so the operator can type partial values
//   ("1.", "") without the field fighting back; conversion to numbers
//   happens only when building the calc input or the save payload.
// @aiInvariant `photoObjectKey` is FORM-DRIVEN (#1207): `contentToForm`
//   seeds it from the loaded content and `formToContent` writes it back
//   (omitted when empty). Carrying it through form state — rather than
//   re-attaching `prior.photoObjectKey` on save — is what lets the photo
//   control mutate (upload/replace/clear) it while an untouched edit still
//   round-trips the existing key.
import {
  type CommercialProposalCalculationInput,
  type CommercialProposalContent,
  type CommercialProposalMode,
  type CommercialProposalPaymentScheme,
  type CommercialProposalTariffUnit,
  type CommercialProposalTotalCurrency,
} from '@magimp/shared';

import { parseCharacteristicsText, serializeCharacteristics } from './characteristics.js';

/**
 * Editable form state. Numeric fields are raw input strings.
 *
 * WHY (#1244): `vatDivisor` / `noVatDivisor` hold a PERCENT string (e.g.
 * `'18'`), NOT the stored divisor. The wire contract still persists a divisor
 * (`divisor = 1 − percent/100`); the percent↔divisor conversion lives in the
 * converters below, bounded to `0 ≤ percent < 100` (D12).
 */
export interface CommercialProposalFormState {
  /** Internal КП composition (#1410): BOTH / BUYOUT / LOGISTICS. */
  mode: CommercialProposalMode;
  proposalDate: string;
  client: string;
  cnyRateRub: string;
  usdRateRub: string;
  buyoutTotalCurrency: CommercialProposalTotalCurrency;
  logisticsTotalCurrency: CommercialProposalTotalCurrency;
  grandTotalCurrency: CommercialProposalTotalCurrency;
  productName: string;
  characteristicsText: string;
  buyoutTerm: string;
  goodsCny: string;
  chinaDeliveryCny: string;
  paymentScheme: CommercialProposalPaymentScheme;
  destination: string;
  logisticsTerm: string;
  weightKg: string;
  volumeM3: string;
  tariffUsdPerKg: string;
  tariffUnit: CommercialProposalTariffUnit;
  packagingUsd: string;
  prrUsd: string;
  servicePercent: string;
  /** Percent string bound to the `vatDivisor` content field (VAT % label). */
  vatDivisor: string;
  /** Percent string bound to the `noVatDivisor` content field (non-VAT %). */
  noVatDivisor: string;
  insurancePercent: string;
  insuranceCnyPerUsdRate: string;
  buyoutNotes: string;
  logisticsNotes: string;
  managerName: string;
  managerPhone: string;
  // Empty string == "no photo bound". `contentToForm` seeds it from the
  // loaded content; `formToContent` omits the wire field when it is blank.
  photoObjectKey: string;
}

/**
 * Parse an input string to a finite number. Blank/whitespace → 0. A fully
 * numeric string (allowing a single decimal comma) → its value. Anything
 * else — trailing garbage or multiple separators — → NaN.
 *
 * WHY: `Number.parseFloat` accepts numeric PREFIXES (`parseFloat('90abc')` is
 * `90`), which would let a malformed visible input be silently saved/previewed
 * as a truncated number. `Number(...)` requires the WHOLE string to be numeric,
 * so malformed input yields NaN and fails the shared `.finite()` schema —
 * surfacing as invalid (submit disabled, preview suppressed) instead of a
 * silent coercion.
 */
export function toNum(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') return 0;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}

function numStr(value: number): string {
  return String(value);
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// WHY (#1244 / D12): the two divisor fields are entered as a PERCENT but stored
// as a divisor (`divisor = 1 − percent/100`). The form bounds the percent to
// `0 ≤ percent < 100`: a percent < 0 would yield divisor > 1 (an unintended
// discount) and a percent ≥ 100 a divisor ≤ 0. Out-of-range (or malformed)
// input maps to NaN so the resulting divisor fails the shared `.positive()`
// schema — surfacing as invalid (submit disabled, preview suppressed) instead
// of silently persisting a bad coefficient.
export function percentToDivisor(rawPercent: string): number {
  const percent = toNum(rawPercent);
  if (!Number.isFinite(percent) || percent < 0 || percent >= 100) return NaN;
  return 1 - percent / 100;
}

// WHY (#1244 / D14): derive the percent shown in the field from a stored
// divisor for DISPLAY. Round only enough to absorb binary-float noise
// (`1 − 0.82 = 0.18000000000000005` → `18`), NEVER to an integer — a genuine
// fractional divisor like `0.875` must show `12.5` and round-trip back exactly.
export function divisorToPercent(divisor: number): string {
  const percent = (1 - divisor) * 100;
  return numStr(Number(percent.toFixed(6)));
}

// WHY (#1244 / D14): on the EDIT path a no-op edit must NOT mutate a stored
// divisor. When the percent field still shows the value derived from the
// original divisor (display form unchanged), persist that divisor VERBATIM —
// a legacy fractional divisor like `0.875` survives a load → save round-trip
// exactly. Only an operator-edited percent recomputes the divisor.
function divisorFor(formPercent: string, priorDivisor: number | undefined): number {
  if (priorDivisor !== undefined && formPercent === divisorToPercent(priorDivisor)) {
    return priorDivisor;
  }
  return percentToDivisor(formPercent);
}

/** A blank create draft: empty numerics, the #1244 defaults, no photo. */
export function emptyFormState(): CommercialProposalFormState {
  // WHY (#1244): build create defaults DIRECTLY rather than cloning the test
  // fixture — a fresh `/kp/new` starts mostly empty with a small set of
  // sensible defaults. Numeric inputs are blank (`''`, == 0 on save); rates
  // stay blank but obligatory (> 0). Manager fields are seeded by the create
  // host from the signed-in user (D13).
  return {
    mode: 'BOTH',
    proposalDate: todayIso(),
    client: '',
    cnyRateRub: '',
    usdRateRub: '',
    buyoutTotalCurrency: 'RUB',
    logisticsTotalCurrency: 'RUB',
    grandTotalCurrency: 'RUB',
    productName: '',
    characteristicsText: '',
    // WHY (#1244 / D9): prototype defaults kept verbatim for unmentioned
    // customer-facing fields, intentionally inlined so the create defaults stay
    // decoupled from the test fixture (`CP_SAMPLE_CONTENT`).
    buyoutTerm: '3-4 рабочих дня',
    goodsCny: '',
    chinaDeliveryCny: '',
    paymentScheme: 'vat',
    destination: 'Москва',
    logisticsTerm: '16-20 дней',
    weightKg: '',
    volumeM3: '',
    tariffUsdPerKg: '',
    tariffUnit: 'kg',
    packagingUsd: '',
    prrUsd: '',
    servicePercent: '5',
    vatDivisor: '18',
    noVatDivisor: '13',
    insurancePercent: '1',
    insuranceCnyPerUsdRate: '6.7',
    buyoutNotes:
      '§ Расчёт производится по курсу в день оплаты\n§ Стоимость доставки по Китаю может измениться',
    logisticsNotes:
      '§ Указанный вес и объём по данным поставщика\n§ Упаковка по согласованию с клиентом',
    managerName: '',
    managerPhone: '',
    photoObjectKey: '',
  };
}

export function contentToForm(content: CommercialProposalContent): CommercialProposalFormState {
  return {
    mode: content.mode,
    proposalDate: content.proposalDate,
    client: content.client,
    cnyRateRub: numStr(content.cnyRateRub),
    usdRateRub: numStr(content.usdRateRub),
    buyoutTotalCurrency: content.buyoutTotalCurrency,
    logisticsTotalCurrency: content.logisticsTotalCurrency,
    grandTotalCurrency: content.grandTotalCurrency,
    productName: content.productName,
    characteristicsText: serializeCharacteristics(content.characteristics),
    buyoutTerm: content.buyoutTerm,
    goodsCny: numStr(content.goodsCny),
    chinaDeliveryCny: numStr(content.chinaDeliveryCny),
    paymentScheme: content.paymentScheme,
    destination: content.destination,
    logisticsTerm: content.logisticsTerm,
    weightKg: numStr(content.weightKg),
    volumeM3: numStr(content.volumeM3),
    tariffUsdPerKg: numStr(content.tariffUsdPerKg),
    tariffUnit: content.tariffUnit,
    packagingUsd: numStr(content.packagingUsd),
    prrUsd: numStr(content.prrUsd),
    servicePercent: numStr(content.servicePercent),
    vatDivisor: divisorToPercent(content.vatDivisor),
    noVatDivisor: divisorToPercent(content.noVatDivisor),
    insurancePercent: numStr(content.insurancePercent),
    insuranceCnyPerUsdRate: numStr(content.insuranceCnyPerUsdRate),
    buyoutNotes: content.buyoutNotes,
    logisticsNotes: content.logisticsNotes,
    managerName: content.managerName,
    managerPhone: content.managerPhone,
    photoObjectKey: content.photoObjectKey ?? '',
  };
}

/**
 * Build the wire `CommercialProposalContent` from the form. `photoObjectKey`
 * is FORM-DRIVEN (#1207): written from `form.photoObjectKey`, omitted when
 * blank, so upload/replace/clear flow through the form and a PATCH carries the
 * current photo state (not a stale prior key). `prior` is retained for the
 * call-site signature but no longer sources the photo key.
 *
 * WHY: numeric inputs are converted verbatim (no silent fallback). A blank /
 * garbage / zero value in a strictly-positive field (rates, divisors) becomes
 * `0`, which fails the shared content schema — so the editor blocks save and
 * hides the live preview instead of silently substituting a default the
 * operator never typed (which would persist wrong totals).
 */
export function formToContent(
  form: CommercialProposalFormState,
  prior: CommercialProposalContent | null,
): CommercialProposalContent {
  const content: CommercialProposalContent = {
    // WHY (#1410): serialize ALL fields regardless of mode — the hidden
    // section's values are RETAINED (never zeroed here); calc + projection do
    // the gating. So a mode switch + save preserves the other section's inputs.
    mode: form.mode,
    proposalDate: form.proposalDate,
    client: form.client,
    cnyRateRub: toNum(form.cnyRateRub),
    usdRateRub: toNum(form.usdRateRub),
    buyoutTotalCurrency: form.buyoutTotalCurrency,
    logisticsTotalCurrency: form.logisticsTotalCurrency,
    grandTotalCurrency: form.grandTotalCurrency,
    productName: form.productName,
    characteristics: parseCharacteristicsText(form.characteristicsText),
    buyoutTerm: form.buyoutTerm,
    goodsCny: toNum(form.goodsCny),
    chinaDeliveryCny: toNum(form.chinaDeliveryCny),
    paymentScheme: form.paymentScheme,
    destination: form.destination,
    logisticsTerm: form.logisticsTerm,
    weightKg: toNum(form.weightKg),
    volumeM3: toNum(form.volumeM3),
    tariffUsdPerKg: toNum(form.tariffUsdPerKg),
    tariffUnit: form.tariffUnit,
    packagingUsd: toNum(form.packagingUsd),
    prrUsd: toNum(form.prrUsd),
    servicePercent: toNum(form.servicePercent),
    vatDivisor: divisorFor(form.vatDivisor, prior?.vatDivisor),
    noVatDivisor: divisorFor(form.noVatDivisor, prior?.noVatDivisor),
    insurancePercent: toNum(form.insurancePercent),
    insuranceCnyPerUsdRate: toNum(form.insuranceCnyPerUsdRate),
    buyoutNotes: form.buyoutNotes,
    logisticsNotes: form.logisticsNotes,
    managerName: form.managerName,
    managerPhone: form.managerPhone,
  };
  // Omit the wire field entirely when no photo is bound; a blank string is
  // not a valid `StorageObjectKey` and "no key" is the absent-field shape.
  const photoObjectKey = form.photoObjectKey.trim();
  if (photoObjectKey !== '') content.photoObjectKey = photoObjectKey;
  return content;
}

/**
 * Project the form onto the pure calc input for the live preview.
 *
 * WHY: rates/divisors are converted verbatim (no fallback); a zero/garbage
 * positive-required field fails the calc-input schema upstream, so the caller
 * suppresses the preview rather than computing totals from a substituted rate.
 * `prior` mirrors `formToContent` (D14): an UNTOUCHED divisor field reuses the
 * stored divisor verbatim, so a legacy divisor > 1 still yields a previewable
 * calc input; an operator-edited percent still goes through the D12 bounds.
 */
export function formToCalcInput(
  form: CommercialProposalFormState,
  prior: CommercialProposalContent | null = null,
): CommercialProposalCalculationInput {
  return {
    mode: form.mode,
    paymentScheme: form.paymentScheme,
    cnyRateRub: toNum(form.cnyRateRub),
    usdRateRub: toNum(form.usdRateRub),
    goodsCny: toNum(form.goodsCny),
    chinaDeliveryCny: toNum(form.chinaDeliveryCny),
    servicePercent: toNum(form.servicePercent),
    weightKg: toNum(form.weightKg),
    volumeM3: toNum(form.volumeM3),
    tariffUsdPerKg: toNum(form.tariffUsdPerKg),
    tariffUnit: form.tariffUnit,
    packagingUsd: toNum(form.packagingUsd),
    prrUsd: toNum(form.prrUsd),
    insurancePercent: toNum(form.insurancePercent),
    noVatDivisor: divisorFor(form.noVatDivisor, prior?.noVatDivisor),
    vatDivisor: divisorFor(form.vatDivisor, prior?.vatDivisor),
    insuranceCnyPerUsdRate: toNum(form.insuranceCnyPerUsdRate),
  };
}
